import type { ProjectView } from "@envstash/core-wasm";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangleIcon, FolderGit2Icon } from "lucide-react";
import { EmptyState, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { projectDisplayPath, timeAgo } from "@/lib/format";
import { useDeviceNames, useDevices, useVault } from "@/lib/hooks";
import { isGone } from "@/lib/projects";

export const Route = createFileRoute("/_app/projects/")({ component: ProjectsPage });

function ProjectsPage() {
  const v = useVault();
  const deviceName = useDeviceNames();
  const devices = useDevices();
  const enrolled = devices.data ? new Set(devices.data.map((d) => d.id)) : null;
  const snap = v.snapshot;
  const conflictsByProject = new Map<string, number>();
  for (const c of snap?.conflicts ?? [])
    conflictsByProject.set(c.project_id, (conflictsByProject.get(c.project_id) ?? 0) + 1);
  const decisionsByProject = new Map<string, number>();
  for (const d of snap?.decisions ?? [])
    decisionsByProject.set(d.project_id, (decisionsByProject.get(d.project_id) ?? 0) + 1);
  const filesByProject = new Map<string, typeof snap extends null ? never : NonNullable<typeof snap>["files"]>();
  for (const f of snap?.files ?? []) filesByProject.set(f.project_id, [...(filesByProject.get(f.project_id) ?? []), f]);
  const projects = [...(snap?.projects ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const hidden = projects.filter((p) => isGone(p, enrolled) || (filesByProject.get(p.id) ?? []).length === 0);
  const live = projects.filter((p) => !hidden.includes(p));

  const card = (p: ProjectView) => {
    const files = filesByProject.get(p.id) ?? [];
    const last = files.map((f) => f.last_change?.wall_ms ?? 0).reduce((a, b) => Math.max(a, b), 0);
    const conflicts = conflictsByProject.get(p.id) ?? 0;
    const decisions = decisionsByProject.get(p.id) ?? 0;
    const gone = isGone(p, enrolled);
    const where = gone
      ? p.checkouts.filter((c) => c.status === "removed").map((c) => `${deviceName(c.device_id)}: ${c.local_path}`)
      : Object.entries(p.device_paths).map(([d, path]) => `${deviceName(d)}: ${path}`);
    return (
      <li key={p.id} data-testid="project-card">
        <Link
          to="/projects/$projectId"
          params={{ projectId: p.id }}
          className="block rounded-xl border bg-card p-4 transition-colors hover:bg-accent/40"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <FolderGit2Icon className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium">{p.name}</span>
            </div>
            <span className="flex items-center gap-1">
              {gone ? <Badge variant="outline">not on any Mac</Badge> : null}
              {decisions > 0 ? (
                <Badge variant="destructive" title="decisions waiting">
                  <AlertTriangleIcon /> {decisions} to decide
                </Badge>
              ) : null}
              {conflicts > 0 ? (
                <Badge variant="destructive" title="conflicting values">
                  <AlertTriangleIcon /> {conflicts}
                </Badge>
              ) : null}
            </span>
          </div>
          <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{projectDisplayPath(p.identity)}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {files.length} file{files.length === 1 ? "" : "s"} · {p.active_devices.length} device
            {p.active_devices.length === 1 ? "" : "s"}
            {last ? ` · changed ${timeAgo(last)}` : ""}
          </p>
          <p className="mt-1 truncate text-xs text-muted-foreground">
            {gone && where.length ? "last at " : ""}
            {where.join(" · ")}
          </p>
        </Link>
      </li>
    );
  };

  return (
    <>
      <PageHeader
        title="Projects"
        description="Each project is identified by its git remote (or home-relative path) so devices agree on what is what."
      />
      {projects.length === 0 ? (
        <EmptyState title="No projects yet">
          The daemon registers a project the first time it finds a tracked file inside it.
        </EmptyState>
      ) : (
        <>
          {live.length === 0 ? (
            <EmptyState title="Nothing is on a Mac right now">
              Every project EnvStash knows is listed below; each comes back here when a folder with it reappears.
            </EmptyState>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">{live.map(card)}</ul>
          )}
          {hidden.length > 0 ? (
            <details className="mt-8" data-testid="hidden-projects">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                Not on any Mac ({hidden.length})
              </summary>
              <p className="mt-1 mb-3 text-xs text-muted-foreground">
                No Mac has these any more, or nothing in them is tracked. History and restore still work, and a project
                comes back above by itself when a folder with it reappears.
              </p>
              <ul className="grid gap-3 sm:grid-cols-2">{hidden.map(card)}</ul>
            </details>
          ) : null}
        </>
      )}
    </>
  );
}
